import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import LogoutIcon from '@mui/icons-material/Logout'

import { MONO } from '../theme/appTheme'

/**
 * The bar across the top: which company you are looking at, a feed warning
 * when the connection is unhealthy, and the theme switch.
 *
 * `syncLabel` is null while the feed is healthy, and then nothing is shown.
 */
export default function TopBar({ company, syncLabel, isDark, onToggleMode, user, onLogout }) {
  const name = user?.user_name || 'Admin'
  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: 'custom.bar',
        color: 'custom.ink',
        borderBottom: '1px solid',
        borderColor: 'custom.line',
      }}
    >
      <Toolbar
        variant="dense"
        disableGutters
        sx={{
          height: 44,
          minHeight: 44,
          '@media all': { minHeight: 44 },
          pl: '20px',
          pr: '16px',
          gap: '12px',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
            {company}
          </Typography>
          {syncLabel && (
            <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.64rem', color: 'custom.ink3' }}>
              {syncLabel}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" sx={{ alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <Tooltip title={isDark ? 'Switch to light' : 'Switch to dark'}>
            <IconButton
              onClick={onToggleMode}
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              sx={{
                width: 26,
                height: 26,
                borderRadius: '6px',
                color: 'custom.ink2',
                '&:hover': { bgcolor: 'custom.chip', color: 'custom.ink' },
              }}
            >
              {isDark ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>

          <Stack direction="row" sx={{ alignItems: 'center', gap: '7px' }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 500, color: 'custom.ink2' }}>
              {name}
            </Typography>
            <Avatar
              sx={{
                width: 22,
                height: 22,
                bgcolor: 'custom.ink',
                color: 'custom.invertInk',
                fontSize: '0.62rem',
                fontWeight: 600,
              }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>
          </Stack>

          {onLogout && (
            <Tooltip title="Sign out">
              <IconButton
                onClick={onLogout}
                aria-label="Sign out"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '6px',
                  color: 'custom.ink2',
                  '&:hover': { bgcolor: 'custom.chip', color: 'custom.ink' },
                }}
              >
                <LogoutIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  )
}
